import { CustomerSite, TenantProfile } from "@hydrologiq/gql-experience/graphql"
import FigureGrid from "@hydrologiq/shared/figures/generic/FigureGrid"
import RunDry from "@hydrologiq/shared/figures/customer/RunDry"
import TimeToDepletion from "@hydrologiq/shared/figures/generic/TimeToDepletion"
import RefuelNeededIn from "@hydrologiq/shared/figures/generic/RefuelNeededIn"
import CustomerOnSite from "@hydrologiq/shared/figures/customer/OnSite"
import CustomerCO2e from "@hydrologiq/shared/figures/customer/CO2e"
import CustomerEnergy from "@hydrologiq/shared/figures/customer/Energy"
import { useGetProfile } from "@hydrologiq/shared/providers/helpers/useProfile"
import Alerts from "@hydrologiq/shared/figures/customer/Alerts"
import { useGetFeature } from "@hydrologiq/shared/providers/helpers/useFeature"
interface CustomerSiteFiguresProps {
  site?: CustomerSite
  loading: boolean
  error: string | undefined
}

// TODO(AAS): move to backend

enum CustomerSiteFigure {
  Alerts = "alerts",
  RunDry = "run_dry", // Legacy - for VMI
  RefuelNeededIn = "refuel_needed_in", // Installation-level for Rental
  TimeToDepletion = "time_to_depletion", // Asset-level for Rental and VMI
  HydrogenOnSite = "hydrogen_on_site",
  CO2e = "co2e",
  Energy = "energy",
}

const getCustomerSiteFigures = (profile: TenantProfile) => {
  const defaultFigures = [CustomerSiteFigure.Alerts, CustomerSiteFigure.TimeToDepletion, CustomerSiteFigure.HydrogenOnSite]
  switch (profile) {
    case TenantProfile.Rental:
      return [
        CustomerSiteFigure.Alerts,
        CustomerSiteFigure.RefuelNeededIn, // Installation-level run_dry
        CustomerSiteFigure.TimeToDepletion, // Asset-level run_dry
        CustomerSiteFigure.CO2e,
        CustomerSiteFigure.Energy,
      ]
    case TenantProfile.FuelProducer:
      return defaultFigures
    default:
      return defaultFigures
  }
}

function CustomerSiteFigures({ site, loading, error }: CustomerSiteFiguresProps) {
  const profile = useGetProfile()
  const figures = getCustomerSiteFigures(profile)
  const assetDepletionEnabled = useGetFeature("asset_depletion")

  return (
    <FigureGrid loading={loading}>
      {figures.includes(CustomerSiteFigure.Alerts) && <Alerts alerts={site?.alerts?.length ?? 0} loading={loading} error={error} />}
      {figures.includes(CustomerSiteFigure.RefuelNeededIn) && (
        <RefuelNeededIn
          heading="Refuel needed in"
          minutes={site?.earliestInstallationRunDryMinutes ?? undefined}
          location={site?.location?.name ?? undefined}
          system={site?.earliestInstallationRunDrySystem?.name}
          systemId={site?.earliestInstallationRunDrySystem?.id}
          granularity="hours"
          loading={loading}
          error={error}
          isPlaceholder={!site?.earliestInstallationRunDryMinutes}
        />
      )}
      {assetDepletionEnabled && figures.includes(CustomerSiteFigure.TimeToDepletion) && (
        <TimeToDepletion
          heading="Asset Depleted in"
          minutes={site?.earliestRunDryMinutes ?? undefined}
          location={site?.location?.name ?? undefined}
          granularity="hours"
          loading={loading}
          error={error}
          isPlaceholder={!site?.earliestRunDryMinutes}
          asset={site?.earliestRunDryAsset ?? undefined}
        />
      )}
      {figures.includes(CustomerSiteFigure.RunDry) && <RunDry context={site} loading={loading} error={error} />}
      {figures.includes(CustomerSiteFigure.HydrogenOnSite) && (
        <CustomerOnSite
          prefix="Hydrogen"
          current={site?.hydrogenOnSiteKg ?? undefined}
          loading={loading}
          error={error}
          mutliple={site?.assetCount ? site.assetCount > 1 : false}
        />
      )}
      {figures.includes(CustomerSiteFigure.CO2e) && <CustomerCO2e context={site} loading={loading} error={error} />}
      {figures.includes(CustomerSiteFigure.Energy) && <CustomerEnergy context={site} loading={loading} error={error} />}
    </FigureGrid>
  )
}

export default CustomerSiteFigures
